import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	input,
	OnDestroy,
	OnInit,
	output,
	signal,
	untracked,
} from '@angular/core';
import { CloseModalSvgComponent } from '@uc/shared/svg';
import { Product, ProductEnum, RadioSelect } from '@uc/web/shared/data-models';
import {
	ClearingCheckboxComponent,
	UcasInputComponent,
	RadioSelectComponent,
	CourseFinderComponent,
	StudyLevelSelectComponent,
} from '@web/shared/ui';
import { Params, Router } from '@angular/router';
import {
	COURSE_SEARCH_FILTER_TOOLTIPS,
	LOCATIONS,
	POSTGRADUATE_DEGREE_LEVELS,
	STUDY_OPTIONS,
	UNDERGRADUATE_DEGREE_LEVELS,
} from '../../static/courses-filter.properties';
import {
	getCourseSearchSegments,
	getRouteSegmentsAndQueryParams,
	getSegmentsWithClearing,
} from '../../utils/route.utils';
import { isPostgraduate } from '../../utils/utils';
import { CourseSearchFilterStateService } from '../../services/course-search-filter-state.service';
import { CourseSearchFilterPillsComponent } from '../course-search-filter-pills/course-search-filter-pills.component';
import { generatePill, generateUcasPointsPill } from '../../utils/pill.utils';
import { CourseSearchFilterPill } from '../../models/course-search-filter.models';
import {
	queryParamsToObject,
	sortQueryParamsByKey,
} from '../../utils/query-params.utils';
import { Store } from '@ngrx/store';
import {
	removeFilterPill,
	updateFilterPills,
} from '../../data-access/+store/course-search.actions';
import { getStateCourseSearchFilterToBeRemoved } from '../../data-access/+store/course-search.selectors';
import {
	getDegreeLevelType,
	getStudyModeType,
	getUniversityRegionType,
} from '../../utils/course-search-payload.utils';
import { MAX_UCAS_TARIFF } from '@uc/web/shared/data-access';
import { CourseSearchAutosuggestService } from '@uc/web/shared/feature/feature-course-search-autosuggest';
import { CourseSearchPayloadValue } from '../../models/course-search-payload.models';
import { HECoSSubjectValidatorService } from '@uc/web/core';
import { CLEARING_ENABLED } from '@uc/web/config';

@Component({
	selector: 'uc-course-search-filter',
	templateUrl: './course-search-filter.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		CloseModalSvgComponent,
		ClearingCheckboxComponent,
		UcasInputComponent,
		RadioSelectComponent,
		CourseFinderComponent,
		StudyLevelSelectComponent,
		CourseSearchFilterPillsComponent,
	],
})
export class CourseSearchFilterComponent implements OnInit, OnDestroy {
	showLocation = input<boolean>(true);
	showDegreeLevel = input<boolean>(true);
	isUniCourseSearch = input<boolean>(false);
	degree = input<string>();
	location = input<string>();
	subject = input<string>();
	studyMode = input<string>();
	ucasPoints = input([undefined, undefined], {
		transform: (value: string | undefined) =>
			value ? value.split('_').map(Number) : [undefined, undefined],
	});
	uniSlug = input<string>();
	disableError = input(false);

	filterChange = output<CourseSearchPayloadValue>();
	filterReset = output<void>();
	autosuggest = output<{ searchTerm: string; degree?: string }>();
	closeFilter = output<boolean>();

	studyLevel = signal<Product | undefined>(ProductEnum.Undergraduate);

	// TODO: refactor to linkedSignal once it's available
	degrees = computed(() => {
		return this.studyLevel() === ProductEnum.Postgraduate
			? signal(structuredClone(POSTGRADUATE_DEGREE_LEVELS))
			: signal(structuredClone(UNDERGRADUATE_DEGREE_LEVELS));
	});
	autoSuggestOpen = computed(() => this._autosuggestSrv.autoSuggestOpen());
	filterPills = computed(() => {
		const pills = this._generateFilterPills();

		untracked(() => {
			this._store.dispatch(updateFilterPills({ pills }));
		});

		return pills;
	});

	filterToBeRemoved = this._store.selectSignal(getStateCourseSearchFilterToBeRemoved);

	locations = structuredClone(LOCATIONS);
	studyOptions = structuredClone(STUDY_OPTIONS);
	tooltips = structuredClone(COURSE_SEARCH_FILTER_TOOLTIPS);
	clearingEnabled = CLEARING_ENABLED;

	// TODO: remove once linkedSignal is available
	private _degreeEffect = effect(() => {
		this.degree();
		untracked(() => {
			this.degrees().update((degrees) =>
				degrees.map((degree) => ({
					...degree,
					checked: degree.link === this.degree(),
				})),
			);
		});
	});

	private _filterToBeRemovedEffect = effect(() => {
		const filterToBeRemoved = this.filterToBeRemoved();
		if (filterToBeRemoved) {
			untracked(() => {
				this.onRemovePill(filterToBeRemoved);
				this._store.dispatch(removeFilterPill({ pill: null }));
			});
		}
	});

	constructor(
		private _router: Router,
		private _store: Store,
		private _autosuggestSrv: CourseSearchAutosuggestService,
		private _hecosSubjectValidatorSrv: HECoSSubjectValidatorService,
		public courseSearchFilterStateSrv: CourseSearchFilterStateService,
	) {}

	ngOnInit(): void {
		this._initialiseStudyLevel();
		this._initialiseStudyOption();
		this._initialiseDegree();
		this._initialiseLocation();
	}

	ngOnDestroy() {
		this._degreeEffect.destroy();
		this._filterToBeRemovedEffect.destroy();
	}

	onFilterState(filterName: string) {
		const _filterName = filterName as keyof CourseSearchFilterStateService;
		const filter = this.courseSearchFilterStateSrv[_filterName];
		if (filter) {
			this.courseSearchFilterStateSrv[_filterName] =
				filter === 'open' ? 'closed' : 'open';
		}
	}

	onClearingSelect(isClearingChecked: boolean) {
		if (isClearingChecked) {
			this.onStudyLevel(ProductEnum.Clearing);
			const { queryParams } = getRouteSegmentsAndQueryParams(this._router.url);
			const segments = getSegmentsWithClearing(this.subject(), this.location());
			this._router.navigate([...segments], { queryParams });

			return;
		}
		this.onStudyLevel(ProductEnum.Undergraduate);
	}

	onStudyLevel(studyLevel: Product) {
		this.studyLevel.set(studyLevel);
		this._resetDegree();
		this.onDegreeSelect(this.degrees()()[0]);
	}

	async onDegreeSelect(degree: RadioSelect) {
		const { queryParams } = getRouteSegmentsAndQueryParams(this._router.url);
		const { link } = degree;
		const subject = this.subject() ?? '';

		const segments = await this._determineSegments(link, subject, this.location());
		const degreeLevel = getDegreeLevelType(link);

		if (isPostgraduate(link) && queryParams['ucaspts']) {
			delete queryParams['ucaspts'];
		}

		this.filterChange.emit({ degreeLevel });
		this._router.navigate([...segments], { queryParams });
	}

	async onLocationSelect(location: RadioSelect) {
		const { queryParams } = getRouteSegmentsAndQueryParams(this._router.url);
		const { link } = location;
		const subject = this.subject() ?? '';

		const segments = await this._determineSegments(this.degree(), subject, link);
		const region = getUniversityRegionType(link);

		this.filterChange.emit({ region });
		this._router.navigate([...segments], { queryParams });
	}

	onUcasPointsSelect(ucasPoints: string) {
		if (!ucasPoints) return;

		const [min = 0, max = MAX_UCAS_TARIFF] = ucasPoints.split('_');

		this.filterChange.emit({ tariffMin: Number(min), tariffMax: Number(max) });
		this._updateQueryParams(`ucaspts=${ucasPoints}`);
	}

	onQueryParamsUpdate(queryParam: RadioSelect) {
		const [key, value] = queryParam.link.split('=');

		if (key === 'mode') {
			const studyMode = getStudyModeType(value);
			this.filterChange.emit({ studyMode });
		}

		this._updateQueryParams(queryParam.link);
	}

	async onRemovePill(pill: CourseSearchFilterPill) {
		switch (pill.filterType) {
			case 'DEGREE':
				await this._resetDegreeAndNavigate();
				break;
			case 'SUBJECT':
				this._resetSubjectAndNavigate();
				break;
			case 'LOCATION':
				await this._resetLocationAndNavigate();
				break;
			case 'STUDY_MODE':
				this._resetStudyOptionAndNavigate();
				break;
			case 'UCAS_POINTS':
				this._resetUcasPointsAndNavigate();
				break;
		}
	}

	async resetFiltersAndNavigate() {
		this._resetDegree();
		this._resetLocation();
		this._resetStudyOption();

		const queryParams: Params = {};
		const subject = this.subject() ?? '';

		const isHECoSSubject =
			await this._hecosSubjectValidatorSrv.isHECoSSubject(subject);

		if (!isHECoSSubject && subject) {
			queryParams['search'] = subject;
		}

		if (this.isUniCourseSearch()) {
			queryParams['uni'] = this.uniSlug();
		}

		const segments = this._determineSegmentsOnFilterReset(isHECoSSubject, subject);

		this.filterReset.emit();
		this._router.navigate([...segments], { queryParams });
	}

	private _updateQueryParams(link: string) {
		const queryParams = this._router.url.split('?')[1];
		const queryParamsObject = queryParamsToObject(`${queryParams}&${link}`);

		if (queryParamsObject) {
			const sortedQueryParams = sortQueryParamsByKey(queryParamsObject);
			this._router.navigate([], {
				queryParams: { ...sortedQueryParams },
			});
		}
	}

	private _resetDegree() {
		this.degrees()().forEach((degree) => (degree.checked = false));

		this.degrees()()[0].checked = true;
	}

	private async _resetDegreeAndNavigate() {
		this._resetDegree();

		const { queryParams } = getRouteSegmentsAndQueryParams(this._router.url);
		const degree = this.degrees()()[0].link;
		const degreeLevel = getDegreeLevelType(degree);
		const subject = this.subject() ?? '';

		const segments = await this._determineSegments(degree, subject, this.location());

		this.filterChange.emit({ degreeLevel });
		this._router.navigate([...segments], { queryParams });
	}

	private _resetLocation() {
		this.locations = this.locations.map((location) => ({
			...location,
			checked: false,
		}));
	}

	private async _resetLocationAndNavigate() {
		this._resetLocation();

		const { queryParams } = getRouteSegmentsAndQueryParams(this._router.url);
		const subject = this.subject() ?? '';
		const degree = this.degree() ?? '';

		const segments = await this._determineSegments(degree, subject, undefined);

		this.filterChange.emit({ region: null });
		this._router.navigate([...segments], { queryParams });
	}

	private _resetUcasPointsAndNavigate() {
		const { segments, queryParams } = getRouteSegmentsAndQueryParams(
			this._router.url,
		);

		if (queryParams && queryParams['ucaspts']) {
			delete queryParams['ucaspts'];

			this.filterChange.emit({ tariffMin: 0, tariffMax: MAX_UCAS_TARIFF });
			this._router.navigate([...segments], { queryParams });
		}
	}

	private _resetStudyOption() {
		this.studyOptions = this.studyOptions.map((option) => ({
			...option,
			checked: false,
		}));
	}

	private _resetStudyOptionAndNavigate() {
		this._resetStudyOption();

		const { segments, queryParams } = getRouteSegmentsAndQueryParams(
			this._router.url,
		);
		if (queryParams && queryParams['mode']) {
			delete queryParams['mode'];

			this.filterChange.emit({ studyMode: null });
			this._router.navigate([...segments], { queryParams });
		}
	}

	private _resetSubjectAndNavigate() {
		if (!this.isUniCourseSearch()) return;

		const { segments, queryParams = {} } = getRouteSegmentsAndQueryParams(
			this._router.url,
		);

		delete queryParams['search'];
		this.filterChange.emit({ name: null });
		this._router.navigate([...segments], { queryParams });
	}

	private _generateFilterPills() {
		const pills = [
			generateUcasPointsPill(this.ucasPoints()),
			generatePill(this.degree(), this.degrees()(), 'DEGREE'),
			generatePill(this.location(), this.locations, 'LOCATION'),
			generatePill(`mode=${this.studyMode()}`, this.studyOptions, 'STUDY_MODE'),
		];

		return pills.filter(Boolean) as CourseSearchFilterPill[];
	}

	private _determineSegmentsOnFilterReset(isHECoSSubject: boolean, subject: string) {
		if (this.isUniCourseSearch()) {
			return ['/courses', this.studyLevel()];
		} else if (!isHECoSSubject) {
			return ['/courses', 'search', this.studyLevel()];
		}

		return ['/courses', 'degrees', this.studyLevel(), subject];
	}

	private async _determineSegments(
		degree: string | undefined,
		subject: string | undefined,
		location: string | undefined,
	) {
		if (this.isUniCourseSearch()) {
			return new Set(['/courses', this.studyLevel()]);
		}

		const _subject = subject ?? '';

		const isHECoSSubject =
			await this._hecosSubjectValidatorSrv.isHECoSSubject(_subject);

		return getCourseSearchSegments(isHECoSSubject, degree, _subject, location);
	}

	private _initialiseStudyOption(): void {
		const studyOption = this.studyMode();

		if (studyOption) {
			this.studyOptions.forEach(
				(option) => (option.checked = option.link.includes(studyOption)),
			);
		}
	}

	private _initialiseDegree(): void {
		this.degrees()().forEach(
			(degree) => (degree.checked = degree.link === this.degree()),
		);
	}

	private _initialiseLocation(): void {
		this.locations.forEach((location) => {
			const isNoLocationProvided = !this.location() && location.link === '';
			if (isNoLocationProvided) {
				location.checked = true;
			} else {
				location.checked = location.link === this.location();
			}
		});
	}

	private _initialiseStudyLevel() {
		const studyLevel = isPostgraduate(this.degree())
			? ProductEnum.Postgraduate
			: ProductEnum.Undergraduate;

		this.studyLevel.set(studyLevel);
	}
}
